import React, { FC, Ref } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Col } from 'react-styled-flexboxgrid';
import { useDebounce } from 'react-use';

import { BUTTON_TYPES, INPUT_TYPES } from '@savgroup-front-common/constants';
import AnimatedRenderer from '@savgroup-front-common/core/atoms/AnimatedRenderer/AnimatedRenderer';
import { Bold } from '@savgroup-front-common/core/atoms/Bold';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { H3 } from '@savgroup-front-common/core/atoms/headings';
import { Tag } from '@savgroup-front-common/core/atoms/Tag';
import { TAG_VARIANT } from '@savgroup-front-common/core/atoms/Tag/Tag.types';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { NumberInputHookForm } from '@savgroup-front-common/core/molecules/Form';
import { DeleteIcon } from '@savgroup-front-common/core/protons/icons';
import { ReturnEligibility } from '@/api/Owner/startOrContinueBusinessReturnCommand';

import { useRemoveProductFromBusinessReturn } from '../CartManager.hooks';
import { CartManagerForm } from '../CartManager.types';

import messages from './messages';
import { useIsOrderLineEligible } from './OrderLine.hooks';
import { $Row } from './OrderLine.styles';

interface OrderLineProps {
  product: {
    orderLineReference: string;
    modelId: string;
    modelName: string;
    batchQuantity?: number;
  };
  eligibility: ReturnEligibility;
  onUpdateQuantity: ({
    modelId,
    batchQuantity,
  }: {
    modelId: string;
    batchQuantity: number;
  }) => void;
  index: number;
  forwardedRef: Ref<HTMLInputElement>;
}

const OrderLine: FC<OrderLineProps> = ({
  product,
  eligibility,
  onUpdateQuantity,
  index,
  forwardedRef,
}) => {
  const isEligible = useIsOrderLineEligible({
    orderLineReference: product.orderLineReference,
    eligibility,
  });

  const formContext = useFormContext<CartManagerForm>();
  const {
    formState: { errors },
    register,
  } = formContext;

  const { handleRemoveProduct, isRemoveProductLoading } =
    useRemoveProductFromBusinessReturn({ formContext });

  const watchedValue = useWatch({
    name: `product.${product.modelId}.batchQuantity`,
  });

  useDebounce(
    () => {
      if (watchedValue !== undefined) {
        onUpdateQuantity({
          modelId: product.modelId,
          batchQuantity: parseInt(watchedValue, 10),
        });
      }
    },
    500,
    [watchedValue],
  );

  return (
    <AnimatedRenderer isOpen>
      <$Row key={product.orderLineReference}>
        <Col xs={8} sm={9}>
          <H3>
            <Bold>{product.modelName}</Bold>
          </H3>
          {isEligible && (
            <Tag variant={TAG_VARIANT.SUCCESS} withoutMargin>
              <SafeFormattedMessageWithoutSpread
                message={messages.isEligible}
              />
            </Tag>
          )}
          {!isEligible && (
            <Tag danger withoutMargin>
              <SafeFormattedMessageWithoutSpread
                message={messages.isNotEligible}
              />
            </Tag>
          )}
        </Col>
        <Col xs={4} sm={2}>
          <NumberInputHookForm
            key={product.orderLineReference}
            name={`product.${product.modelId}.batchQuantity`}
            errors={errors}
            min={0}
            selectAllOnFocus
            register={register}
            defaultValue={product.batchQuantity || 1}
            type={INPUT_TYPES.NUMBER}
            tabIndex={index}
            ref={forwardedRef}
          />
        </Col>
        <Col xs={false} sm={1}>
          <Button
            tertiary
            icon={<DeleteIcon />}
            type={BUTTON_TYPES.BUTTON}
            isLoading={isRemoveProductLoading}
            onClick={async () => {
              await handleRemoveProduct({ modelId: product.modelId });
            }}
          />
        </Col>
      </$Row>
    </AnimatedRenderer>
  );
};

OrderLine.displayName = 'OrderLine';

export default React.forwardRef<
  HTMLInputElement,
  Omit<OrderLineProps, 'forwardedRef'>
>((props, ref) => <OrderLine {...props} forwardedRef={ref} />);
